import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  hideRouteAlert,
  RouteAlert,
  selectRouteAlert,
} from "@/reducers/overlaysSlice"
import { useCallback, useEffect, useRef, useState } from "react"
import MapView, { MapViewHandle } from "@/components/maps/MapView"
import AdHocCommands from "@/api/adHocCommands"
import RoutePolylinesParser from "@/lib/routePolylinesPareser"
import { RouteInfo } from "./RouteView"
import { Route2g, RouteStop } from "@/reducers/routes2gSlice"
import { Source } from "./mapLibre/Source"
import { Layer, LayerProps } from "./mapLibre/Layer"
import { Feature, Geometry } from "geojson"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  selectActiveChat,
  selectDriverParticipant,
} from "@/reducers/chatsSliceSelectors"
import { format } from "date-fns"
import { convertToRoute2g } from "@/components/maps/SharedMap"
import { LngLatBounds } from "maplibre-gl"
import * as polyline from "@mapbox/polyline"
import PulsingDot from "@/components/maps/PulsingDot"
import { FinishedRouteAlert } from "@/reducers/messagesSlice"
import DriverPolylineParser from "@/lib/driverPolylineParser"
import { Participant } from "@/reducers/chatsSlice"
import {
  filterLegsByStops,
  getFinishAlertFallbackRouteTimestamp,
  getFinishAlertReferenceRoute,
  loadOrderRoute,
} from "./finishAlertRoute"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"

const driverLocationLayer: LayerProps = {
  id: "location-point",
  type: "symbol",
  layout: {
    "icon-image": "pulsing-dot",
  },
}

const driverRouteLayer: LayerProps = {
  id: "driver-route-layer",
  type: "line",
  layout: {
    visibility: "visible",
  },
  paint: {
    "line-color": "#960018",
    "line-width": 3,
    "line-opacity": 1,
  },
}

function FinishAlert({
  providedFinishedRouteAlert,
  onClose,
}: {
  providedFinishedRouteAlert?: RouteAlert
  onClose?: () => void
}) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [, setIsLoading] = useState(true)
  const [, setError] = useState<Error | null>(null)
  const mapViewRef = useRef<MapViewHandle>(null)
  const activeRouteAlert = useAppSelector(selectRouteAlert)
  const finishedRouteAlert = providedFinishedRouteAlert || activeRouteAlert

  const { expectedDistance, actualDistance } = (finishedRouteAlert.details ||
    {}) as FinishedRouteAlert

  const [stops, setStops] = useState<Route2g | undefined>(undefined)
  const [routeInfo, setRouteInfo] = useState<RouteInfo | undefined>(undefined)
  const [driverLocationSource, setDriverLocationSource] = useState<
    Feature<Geometry, { [p: string]: any }> | undefined
  >(undefined)
  const [driverRouteSource, setDriverRouteSource] = useState<
    Feature<Geometry, { [p: string]: any }> | undefined
  >(undefined)

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose()
      return
    }

    dispatch(hideRouteAlert())
  }, [dispatch])

  // Draw reference route on the map - using polling to check style loaded

  const refreshDriverLocation = useCallback(() => {
    if (driverLocationSource) {
      setDriverLocationSource({ ...driverLocationSource })
    }
  }, [driverLocationSource])

  const fetchAlertData = useCallback(async () => {
    const { driverJid, alertId, details } = finishedRouteAlert
    const typedDetails = details as FinishedRouteAlert
    const { startTimestamp, endTimestamp } = typedDetails
    if (!driverJid || !alertId) return

    setIsLoading(true)
    setError(null)
    // Clear data from a previously shown alert - without this, when the
    // alert changes (or a fetch fails) the map keeps drawing the previous
    // alert's planned/actual routes under the new alert's header.
    setStops(undefined)
    setRouteInfo(undefined)
    setDriverLocationSource(undefined)
    setDriverRouteSource(undefined)

    try {
      const referenceRoute = getFinishAlertReferenceRoute(
        typedDetails,
        driverJid,
      )
      const loadOrderLegs = async (routeTimestamp: string) => {
        const result = await AdHocCommands.getRoutePolylines(
          window.XMPP!,
          referenceRoute.driverJid,
          routeTimestamp,
        )

        const routeInfo = RoutePolylinesParser.parse(result)
          ?.route as RouteInfo & {
          driver_location: { lat: number; lng: number }
          stops: RouteStop[]
        }
        if (!routeInfo?.routes?.[0].legs) return undefined

        return filterLegsByStops(
          routeInfo,
          referenceRoute.firstStopId,
          referenceRoute.lastStopId,
        )
      }

      const strippedRouteInfo = await loadOrderRoute(
        referenceRoute.routeTimestamp,
        getFinishAlertFallbackRouteTimestamp(typedDetails, referenceRoute),
        loadOrderLegs,
      )

      if (strippedRouteInfo && driverJid) {
        const strippedStops = strippedRouteInfo.routes[0].legs.map(
          (leg) => leg.stop,
        )

        setStops(convertToRoute2g(strippedStops, driverJid))
        setRouteInfo(strippedRouteInfo)
      }

      const driverPolylinesResult = await AdHocCommands.getDriverPolylines(
        window.XMPP!,
        driverJid,
        startTimestamp,
        endTimestamp,
      )
      const driverPolyline = DriverPolylineParser.parse(driverPolylinesResult)
      if (driverPolyline?.polyline) {
        const driverRouteCoordinates = polyline
          .decode(driverPolyline?.polyline)
          .map(([lat, lng]) => [lng, lat])

        setDriverRouteSource({
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: driverRouteCoordinates,
          },
        })
      }
    } catch (err) {
      console.error("Error fetching route for alert:", err)
    } finally {
      setIsLoading(false)
    }
  }, [finishedRouteAlert])

  // Refetch whenever the displayed alert changes - the component is reused
  // without remounting when the user opens the map for another alert.
  useEffect(() => {
    fetchAlertData()
  }, [fetchAlertData])

  const showAllLayers = useCallback(() => {
    const mapInstance = mapViewRef.current?.getMapManager()?.getMapInstance()
    if (!mapInstance) return

    const bounds = new LngLatBounds()

    if (routeInfo) {
      routeInfo.routes[0].legs
        .map((leg) => polyline.decode(leg.polyline))
        .flatMap((polyline) => (Array.isArray(polyline) ? polyline : []))
        .forEach(([lat, lng]) => bounds.extend([lng, lat] as [number, number]))
    }

    // The driven route too: when the planned route has none of the order's
    // stops, fitting to it alone left the actual route off-screen.
    if (driverRouteSource?.geometry.type === "LineString") {
      driverRouteSource.geometry.coordinates.forEach(([lng, lat]) =>
        bounds.extend([lng, lat] as [number, number]),
      )
    }

    if (!bounds.isEmpty()) {
      mapInstance.fitBounds(bounds, {
        padding: 40,
        maxZoom: 15,
        duration: 1000,
      })
    }

    if (driverLocationSource) {
      if (!mapInstance.hasImage("pulsing-dot")) {
        const pulsingDotImage = PulsingDot(mapInstance)
        mapInstance.addImage("pulsing-dot", pulsingDotImage, {
          pixelRatio: 2,
        })
        setTimeout(() => refreshDriverLocation(), 1000)
      }
    }
  }, [
    routeInfo,
    driverRouteSource,
    driverLocationSource,
    refreshDriverLocation,
  ])

  useEffect(() => {
    requestAnimationFrame(() => showAllLayers())
  }, [routeInfo, driverRouteSource, showAllLayers])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose()
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleClose])
  const alertDate = finishedRouteAlert.updatedAt
    ? format(new Date(finishedRouteAlert.updatedAt), "HH:mm dd/MM/yyyy", {
        locale: getDateFnsLocale(),
      })
    : ""

  const difference = actualDistance && actualDistance - expectedDistance
  const differenceText =
    difference && difference !== 0
      ? ` (${difference > 0 ? "+" : "-"}${Math.abs(difference / 1000).toFixed(0)}km)`
      : ""

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        }
      }}
    >
      <DialogContent
        className="max-h-[90vh] sm:max-w-[50vw]"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
        backgroundOpacity={50}
      >
        <DialogHeader>
          <DialogTitle>{t("maps.finishAlert.title")}</DialogTitle>
          <DialogDescription>
            <div className="flex gap-1 pb-4 pt-2 text-muted-foreground">
              <span>{alertDate}</span>
            </div>
            <div className="gap-1 pb-4 pt-2 text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-0.5 w-4 bg-blue-500" />
                {t("maps.finishAlert.plannedRoute")}{" "}
                <span className="font-mono tabular-nums">
                  {Math.abs(expectedDistance / 1000).toFixed(0)}km
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex h-0.5 w-4 bg-red-700" />
                {t("maps.finishAlert.actualRoute")}
                <span className="font-mono tabular-nums">
                  {actualDistance === undefined
                    ? t("maps.finishAlert.notAvailable")
                    : `${Math.abs(actualDistance / 1000).toFixed(0)}km`}
                </span>{" "}
                <span
                  className={
                    difference && difference > 0
                      ? "font-mono tabular-nums text-destructive"
                      : "font-mono tabular-nums text-green-700"
                  }
                >
                  {differenceText}
                </span>
              </div>
            </div>
            <div className="relative mx-auto flex h-[70vh] w-full">
              {routeInfo && (
                <MapView
                  isAlertMap
                  mapViewRef={mapViewRef}
                  sharedStops={stops}
                  sharedRoute={routeInfo}
                  driverJid="shared"
                  isSharedMap
                >
                  {driverRouteSource && (
                    <Source type="geojson" data={driverRouteSource}>
                      <Layer {...driverRouteLayer} />
                    </Source>
                  )}
                  {driverLocationSource && (
                    <Source type="geojson" data={driverLocationSource}>
                      <Layer {...driverLocationLayer} />
                    </Source>
                  )}
                </MapView>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}

export default FinishAlert
